import { Component, DestroyRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GraphqlService } from '../../service/graphql.service';
import { Category } from '../../model/category';
import { User } from '../../model/user';
import { CategoryModalComponent } from './category-modal/category-modal.component';
import { CategoryPath } from '../../model/categorypath';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { finalize, map, Observable, startWith } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { UserService } from '../../service/user.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSelect } from '@angular/material/select';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatMenuModule } from '@angular/material/menu';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointQuery } from '@/model/breakpoint-query';
import { BreakpointObserver } from '@angular/cdk/layout';
import { LoadingIndicationComponent } from '../components/loading-indication/loading-indication.component';
import { AdvisoryBezirkSelectorComponent } from '../components/advisory-bezirk-selector/advisory-bezirk-selector.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

interface CategoryNode {
  id: string;
  name: string;
  children?: CategoryNode[];
  level?: number;
  expandable?: boolean;
}
@Component({
  selector: 'app-admin-view',
  imports: [
    CategoryModalComponent,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    CommonModule,
    MatIcon,
    FormsModule,
    MatSelect,
    MatTreeModule,
    MatMenuModule,
    LoadingIndicationComponent,
    AdvisoryBezirkSelectorComponent,
    MatSlideToggleModule,
  ],
  templateUrl: './admin-view.component.html',
  styleUrl: './admin-view.component.scss',
  standalone: true,
})
export class AdminViewComponent implements OnInit {
  categories: Category[] = [];
  path: CategoryPath[] = [];
  currentCategoryName: string | null = null;
  currentCategoryId: string | null = null;
  showModal = false;
  isEditing = false;
  showDeleteModal = false;
  userIdToDelete: string | null = null;
  activeModalSection = 'info';
  availableRoles: string[] = ['ADMIN', 'COMPANY'];

  filteredCategories!: Observable<Category[]>;

  currentParentId: string | null = null;
  modalCategory: Category | null = null;
  users: User[] = [];
  usersPerPage = 5;
  offset = 0;
  hasMoreUsers = true;
  showUserModal = false;
  currentUserId: string | null = null;

  userForm: FormGroup;

  treeControl = new NestedTreeControl<CategoryNode>((node) => node.children);
  dataSource = new MatTreeNestedDataSource<CategoryNode>();

  // Keep all categories in memory
  allCategories: Category[] = [];
  isMobileView: boolean | undefined;

  // Filter control
  searchControl = new FormControl('');

  hasChild = (_: number, node: CategoryNode) =>
    !!node.children && node.children.length > 0;

  getNodeLevel(node: CategoryNode): { isRoot: boolean; level: number } {
    // Helper function to find node in tree
    const findNodeLevel = (
      nodes: CategoryNode[],
      targetNode: CategoryNode,
      currentLevel = 0,
    ): number | null => {
      for (const n of nodes) {
        if (n === targetNode) {
          return currentLevel;
        }
        if (n.children) {
          const childLevel = findNodeLevel(
            n.children,
            targetNode,
            currentLevel + 1,
          );
          if (childLevel !== null) {
            return childLevel;
          }
        }
      }
      return null;
    };

    const level = findNodeLevel(this.dataSource.data, node);
    return {
      isRoot: level === 0,
      level: level || 0,
    };
  }

  isLoadingUsers = false;
  isLoadingCategories = false;
  isSavingUser = false;

  // User management filters
  userSearchTerm = '';
  showOnlyAdvisoryboard = false;

  constructor(
    private fb: FormBuilder,
    private graphqlService: GraphqlService,
    private userService: UserService,
    private snackBar: MatSnackBar,
    private breakpointObserver: BreakpointObserver,
    private destroyRef: DestroyRef,
  ) {
    this.userForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: [''],
      email: ['', [Validators.required, Validators.email]],
      birthYear: [
        '',
        [
          Validators.required,
          Validators.min(1900),
          Validators.max(new Date().getFullYear()),
        ],
      ],
      zipCode: [''],
      isAdvisoryboard: [false],
      isRegionalAdvisoryboard: [false],
      hasAdminRole: [false],
      hasCompanyRole: [false],
      advisoryCategories: [[]],
      advisoryBezirkIds: [[]],
      // exclusion-only; no advisoryMode
    });
  }

  ngOnInit() {
    this.breakpointObserver
      .observe(BreakpointQuery.Compact)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => (this.isMobileView = state.matches));

    this.loadCategories();
    this.loadUsers();
    this.filteredCategories = this.searchControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || '')),
    );

    this.searchControl.valueChanges.subscribe((value) => {
      this.filterCategories(value || '');
    });
  }

  loadCategories(): void {
    this.isLoadingCategories = true;
    this.graphqlService.allCategories().subscribe({
      next: (categories: Category[]) => {
        categories.sort((a, b) => a.name.localeCompare(b.name));
        this.allCategories = categories;
        this.buildAndSetTree(categories);
      },
      error: (error) => {
        console.error('Error loading categories:', error);
        this.snackBar.open(
          'Failed to load categories. Please try again.',
          'Close',
          {
            duration: 3000,
            panelClass: ['snackbar-error'],
          },
        );
      },
      complete: () => {
        this.isLoadingCategories = false;
      },
    });
  }

  /**
   * Build a nested tree from a flat list of categories
   */
  private buildTree(
    categories: Category[],
    parentId: string | null,
  ): CategoryNode[] {
    return categories
      .filter((cat) => cat.parentId === parentId)
      .map((cat) => {
        const children = this.buildTree(categories, cat.id);
        return {
          id: cat.id,
          name: cat.name,
          children: children.length ? children : undefined,
        };
      });
  }

  private buildAndSetTree(categories: Category[]): void {
    this.dataSource.data = this.buildTree(categories, null);
    this.treeControl.dataNodes = this.dataSource.data;
    this.treeControl.expandAll(); // optionally expand all by default
  }

  /**
   * Filter the categories based on the search term, but keep parents of matched items
   */
  private filterCategories(term: string): void {
    term = term.toLowerCase().trim();
    if (!term) {
      // Reset to unfiltered
      this.buildAndSetTree(this.allCategories);
      return;
    }

    // 1) Find all categories whose name matches
    const matchedCategories = this.allCategories.filter((cat) =>
      cat.name.toLowerCase().includes(term),
    );

    // 2) Collect all ancestor IDs for those matched categories
    const keepIds = new Set<string>();
    matchedCategories.forEach((match) => {
      keepIds.add(match.id);
      this.addAncestorsToSet(this.allCategories, match, keepIds);
    });

    // 3) Filter original categories to only keep those with an ID in keepIds
    //    (i.e., each matched cat plus its ancestor chain).
    const filtered = this.allCategories.filter((cat) => keepIds.has(cat.id));

    // 4) Build the tree from the filtered categories
    this.buildAndSetTree(filtered);
  }

  /**
   * Recursively climb up parent chain, adding all ancestors' IDs to keepIds
   */
  private addAncestorsToSet(
    allCats: Category[],
    category: Category,
    keepIds: Set<string>,
  ): void {
    if (!category.parentId) {
      return;
    }
    keepIds.add(category.parentId);
    const parent = allCats.find((c) => c.id === category.parentId);
    if (parent) {
      this.addAncestorsToSet(allCats, parent, keepIds);
    }
  }

  /**
   * CRUD operations
   */
  addRootCategory() {
    this.openModal();
  }

  addSubcategory(node: CategoryNode) {
    const parentCategory = this.allCategories.find((cat) => cat.id === node.id);
    const newCategory: Category = {
      id: '',
      name: '',
      description: '',
      parentId: parentCategory?.id,
    };
    this.openModal(newCategory);
  }

  editCategory(node: CategoryNode) {
    const category = this.allCategories.find((cat) => cat.id === node.id);
    this.openModal(category);
  }

  deleteCategory(node: CategoryNode) {
    console.log('Delete node:', node);
    if (confirm(`Möchten Sie die Kategorie "${node.name}" wirklich löschen?`)) {
      console.log('Delete node:', node);
      this.graphqlService.deleteCategory(node.id).subscribe({
        next: (res) => {
          if (res.data.deleteCategory) {
            this.loadCategories();
          } else {
            this.snackBar.open(
              'Fehler beim Löschen der Kategorie, sie ist noch verknüpft.',
              'Schließen',
              {
                duration: 3000,
                panelClass: ['snackbar-error'],
              },
            );
          }
        },
        error: (error) => {
          console.error('Error deleting category:', error);
        },
      });
    }
  }

  clearSearch() {
    this.searchControl.setValue('');
  }

  /**
   * QoL: Select all categories for advisory board at once
   */
  selectAllAdvisoryCategories(): void {
    const allIds = this.allCategories.map((c) => c.id);
    this.userForm.get('advisoryCategories')?.setValue(allIds);
    this.userForm.get('advisoryCategories')?.markAsDirty();
    this.userForm.get('advisoryCategories')?.markAsTouched();
  }

  /**
   * QoL: Clear all selected advisory categories
   */
  clearAllAdvisoryCategories(): void {
    this.userForm.get('advisoryCategories')?.setValue([]);
    this.userForm.get('advisoryCategories')?.markAsDirty();
    this.userForm.get('advisoryCategories')?.markAsTouched();
  }

  /**
   * Helper for UI toggles to know if everything is selected
   */
  areAllAdvisoryCategoriesSelected(): boolean {
    const selected: string[] =
      this.userForm.get('advisoryCategories')?.value || [];
    return selected.length > 0 && selected.length === this.allCategories.length;
  }

  activeSection: string | null = 'section1';

  toggle(section: string) {
    this.activeSection = section;
  }

  openModal(category?: Category) {
    this.isEditing = false;
    if (category) {
      console.log('Editing category:', category);
      this.modalCategory = { ...category }; // copy the category
    } else {
      console.log('Adding a new category');
      this.modalCategory = null;
    }
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.modalCategory = null;
    this.loadCategories();
  }

  loadUsers(): void {
    this.isLoadingUsers = true;
    // Reset the list and pagination to show empty state while loading
    this.users = [];
    this.offset = 0;
    this.hasMoreUsers = true;
    this.userService.getAllUsers().subscribe({
      next: (users: User[]) => {
        this.users = users;
      },
      error: (error) => {
        console.error('Error loading users:', error);
        this.snackBar.open('Failed to load users. Please try again.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error'],
        });
      },
      complete: () => {
        this.isLoadingUsers = false;
      },
    });
  }

  loadMoreUsers(): void {
    this.isLoadingUsers = true;
    this.userService.getAllUsers(this.offset, this.usersPerPage).subscribe({
      next: (fetchedUsers: User[]) => {
        const existingUserIds = new Set(this.users.map((user) => user.id));
        fetchedUsers = fetchedUsers.filter(
          (user) => !existingUserIds.has(user.id),
        );
        this.users = [...this.users, ...fetchedUsers];
        this.offset += this.usersPerPage;
        this.hasMoreUsers = fetchedUsers.length === this.usersPerPage;
      },
      error: (error) => {
        console.error('Error loading more users:', error);
        this.snackBar.open(
          'Failed to load more users. Please try again.',
          'Close',
          {
            duration: 3000,
            panelClass: ['snackbar-error'],
          },
        );
      },
      complete: () => {
        this.isLoadingUsers = false;
      },
    });
  }

  // Recursively build the nested structure
  private buildCategoryTree(
    categories: Category[],
    parentId: string | null,
  ): CategoryNode[] {
    return categories
      .filter((cat) => cat.parentId === parentId)
      .map((cat) => {
        return {
          name: cat.name,
          // Recursively get children
          children: this.buildCategoryTree(categories, cat.id),
        } as CategoryNode;
      });
  }

  private _filter(value: string): Category[] {
    const filterValue = value.toLowerCase();
    return this.categories.filter((category) =>
      category.name.toLowerCase().includes(filterValue),
    );
  }

  /**
   * Filtered users for the management table (search + Fachbeirat only)
   */
  get filteredUsers(): User[] {
    const term = this.userSearchTerm?.trim().toLowerCase() ?? '';

    return this.users.filter((user) => {
      // Fachbeirat-only filter
      if (this.showOnlyAdvisoryboard && !user.isAdvisoryboard) {
        return false;
      }

      // Text search over firstName, lastName, email
      if (!term) {
        return true;
      }

      const firstName = user.firstName?.toLowerCase() ?? '';
      const lastName = user.lastName?.toLowerCase() ?? '';
      const email = user.email?.toLowerCase() ?? '';

      return (
        firstName.includes(term) ||
        lastName.includes(term) ||
        email.includes(term)
      );
    });
  }

  openUserModal(user?: User): void {
    console.log(user);
    this.isEditing = !!user;
    this.currentUserId = user ? user.id : null;
    this.userForm.reset();
    this.activeModalSection = 'info';

    if (user) {
      this.userForm.patchValue(user);
      // prefill advisory selections
      const excluded = user.advisoryExcludedBezirkIds || [];
      this.userForm
        .get('advisoryBezirkIds')
        ?.setValue(excluded.map((x) => Number(x)));
      if (user.roles && user.roles.includes('ADMIN')) {
        console.log('Admin role selected');
        this.userForm.get('hasAdminRole')?.setValue(true);
      }

      if (user.roles && user.roles.includes('COMPANY')) {
        console.log('Company role selected');
        this.userForm.get('hasCompanyRole')?.setValue(true);
      }
    } else {
      this.userForm.reset();
      // default to empty exclusion list
      this.userForm.get('advisoryBezirkIds')?.setValue([]);
    }

    this.showUserModal = true;
  }

  closeUserModal(): void {
    this.showUserModal = false;
    this.userForm.reset();
  }

  deleteUser(userId: string): void {
    if (confirm('Are you sure you want to delete this user?')) {
      this.userService.deleteUser(userId).subscribe(
        () => {
          this.users = this.users.filter((user) => user.id !== userId);
        },
        (error) => {
          console.error('Error deleting user:', error);
        },
      );
    }
  }

  updateUser(user: User): void {
    if (this.userForm.valid) {
      this.userService.updateUser(user).subscribe(
        () => {
          const index = this.users.findIndex((u) => u.id === user.id);
          if (index !== -1) {
            this.users[index] = { ...user };
          }
          this.closeUserModal();
          this.snackBar.open('User updated successfully.', 'Close', {
            duration: 3000,
            panelClass: ['snackbar-success'],
          });
        },
        (error) => {
          console.error('Error updating user:', error);
          this.snackBar.open(
            'Failed to update user. Please try again.',
            'Close',
            {
              duration: 3000,
              panelClass: ['snackbar-error'],
            },
          );
        },
      );
    }
  }

  confirmUpdateUser(): void {
    if (this.isSavingUser) {
      return; // debounce/guard against repeated clicks while saving
    }

    if (this.userForm.valid) {
      this.isSavingUser = true;
      this.userForm.markAllAsTouched();
      const user = { ...this.userForm.value, id: this.currentUserId };

      if (this.isEditing) {
        const roles: string[] = [];

        if (this.userForm.get('hasAdminRole')?.value) {
          roles.push('ADMIN');
        }
        if (this.userForm.get('hasCompanyRole')?.value) {
          roles.push('COMPANY');
        }

        // Assign the roles array to user.roles
        user.roles = roles;
        // advisory include/exclude wiring
        const ids: number[] =
          this.userForm.get('advisoryBezirkIds')?.value || [];
        user.advisoryExcludedBezirkIds = ids.map(String);
        this.userService
          .updateUser(user)
          .pipe(finalize(() => (this.isSavingUser = false)))
          .subscribe(
            () => {
              this.closeUserModal();
              this.snackBar.open('User updated successfully.', 'Close', {
                duration: 3000,
                panelClass: ['snackbar-success'],
              });
              this.loadUsers();
            },
            (error) => {
              console.error('Error updating user:', error);
              this.snackBar.open(
                'Failed to update user. Please try again.',
                'Close',
                {
                  duration: 3000,
                  panelClass: ['snackbar-error'],
                },
              );
            },
          );
      } else {
        // Call addUser if adding a new user
        // advisory include/exclude wiring for create
        const ids: number[] =
          this.userForm.get('advisoryBezirkIds')?.value || [];
        user.advisoryExcludedBezirkIds = ids.map(String);

        this.userService
          .addUser(user)
          .pipe(finalize(() => (this.isSavingUser = false)))
          .subscribe(
            () => {
              this.closeUserModal();
              this.snackBar.open('User added successfully.', 'Close', {
                duration: 3000,
                panelClass: ['snackbar-success'],
              });
              this.loadUsers();
            },
            (error) => {
              console.error('Error adding user:', error);
              this.snackBar.open(
                'Failed to add user. Please try again.',
                'Close',
                {
                  duration: 3000,
                  panelClass: ['snackbar-error'],
                },
              );
            },
          );
      }
    } else {
      this.userForm.markAllAsTouched();
      this.isSavingUser = false;
    }
  }

  openDeleteModal(userId: string): void {
    this.userIdToDelete = userId;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.userIdToDelete = null;
  }

  confirmDeleteUser(): void {
    if (this.userIdToDelete) {
      this.userService.deleteUser(this.userIdToDelete).subscribe(
        () => {
          this.users = this.users.filter(
            (user) => user.id !== this.userIdToDelete,
          );
          this.closeDeleteModal();
          this.snackBar.open('User deleted successfully.', 'Close', {
            duration: 3000,
            panelClass: ['snackbar-success'],
          });
        },
        (error) => {
          console.error('Error deleting user:', error);
          this.snackBar.open(
            'Failed to delete user. Please try again.',
            'Close',
            {
              duration: 3000,
              panelClass: ['snackbar-error'],
            },
          );
        },
      );
    }
  }
  toggleAdvisoryTab(): void {
    if (this.userForm.get('isAdvisoryboard')?.value) {
      this.activeModalSection = 'advisory';
    } else {
      this.userForm.get('advisoryCategories')?.setValue([]);
      this.activeModalSection = 'info';
    }
  }
}
